// Nevades — global runtime
(() => {
  "use strict";

  // ----- Helpers -----
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const formatPrice = (cents) =>
    (cents / 100).toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });

  // ----- Cookie banner -------------------------------------------------------
  const cookieBanner = $("[data-cookie-banner]");
  if (cookieBanner) {
    try {
      const choice = localStorage.getItem("nevades-cookies");
      if (!choice) {
        cookieBanner.classList.remove("hidden");
      }
    } catch {}
    const dismiss = (value) => {
      try {
        localStorage.setItem("nevades-cookies", value);
      } catch {}
      cookieBanner.classList.add("hidden");
    };
    cookieBanner.querySelector("[data-cookie-accept]")?.addEventListener("click", () => dismiss("accepted"));
    cookieBanner.querySelector("[data-cookie-reject]")?.addEventListener("click", () => dismiss("rejected"));
  }

  // ----- Back to top button --------------------------------------------------
  const backToTop = $("[data-back-to-top]");
  if (backToTop) {
    const onScroll = () => {
      if (window.scrollY > 600) {
        backToTop.classList.remove("opacity-0", "pointer-events-none", "translate-y-2");
        backToTop.classList.add("opacity-100");
      } else {
        backToTop.classList.add("opacity-0", "pointer-events-none", "translate-y-2");
        backToTop.classList.remove("opacity-100");
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    backToTop.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  // ----- Header scroll state -----
  const header = $("[data-header]");
  if (header) {
    const onScroll = () => {
      if (window.scrollY > 8) {
        header.classList.add("bg-cream/85", "border-ink-08", "backdrop-blur-md");
        header.classList.remove("bg-cream/0", "border-transparent");
      } else {
        header.classList.remove("bg-cream/85", "border-ink-08", "backdrop-blur-md");
        header.classList.add("bg-cream/0", "border-transparent");
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  // ----- Mobile menu drawer -----
  const mobileDrawer = $("[data-mobile-drawer]");
  const mobilePanel = $("[data-mobile-drawer-panel]");
  $$("[data-mobile-menu-open]").forEach((btn) =>
    btn.addEventListener("click", () => {
      if (!mobileDrawer || !mobilePanel) return;
      mobileDrawer.classList.remove("opacity-0", "pointer-events-none");
      mobileDrawer.classList.add("opacity-100");
      mobilePanel.classList.remove("translate-x-full");
      document.body.style.overflow = "hidden";
    })
  );
  $$("[data-mobile-menu-close]").forEach((btn) =>
    btn.addEventListener("click", () => {
      if (!mobileDrawer || !mobilePanel) return;
      mobileDrawer.classList.add("opacity-0", "pointer-events-none");
      mobileDrawer.classList.remove("opacity-100");
      mobilePanel.classList.add("translate-x-full");
      document.body.style.overflow = "";
    })
  );

  // ----- Reveal on scroll -----
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -10% 0px" }
    );
    $$(".reveal").forEach((el) => io.observe(el));
  }

  // ----- Hero crossfade rotation -----
  const hero = $("[data-hero]");
  if (hero) {
    const images = $$("[data-hero-image]", hero);
    const dots = $$("[data-hero-dot]", hero);
    const counter = $("[data-hero-counter]", hero);
    let index = 0;
    let timer = null;
    const total = images.length;
    const pad = (n) => String(n).padStart(2, "0");

    const goTo = (i) => {
      index = i % total;
      images.forEach((img, k) => {
        img.classList.toggle("opacity-100", k === index);
        img.classList.toggle("opacity-0", k !== index);
      });
      dots.forEach((dot, k) => {
        const bar = dot.querySelector("span");
        if (!bar) return;
        if (k === index) {
          bar.classList.add("w-12", "bg-accent");
          bar.classList.remove("w-6", "bg-anthracite/30");
        } else {
          bar.classList.remove("w-12", "bg-accent");
          bar.classList.add("w-6", "bg-anthracite/30");
        }
      });
      if (counter) counter.textContent = `${pad(index + 1)} / ${pad(total)}`;
    };

    const start = () => {
      if (timer) clearInterval(timer);
      if (total < 2) return;
      timer = setInterval(() => goTo(index + 1), 10000);
    };

    dots.forEach((dot, k) =>
      dot.addEventListener("click", () => {
        goTo(k);
        start();
      })
    );

    start();
  }

  // ----- Cart drawer ---------------------------------------------------------
  const cartDrawer = {
    el: () => $("[data-cart-drawer]"),
    panel: () => $("[data-cart-drawer-panel]"),

    open() {
      const el = this.el();
      const panel = this.panel();
      if (!el || !panel) return;
      el.classList.remove("opacity-0", "pointer-events-none");
      el.classList.add("opacity-100");
      panel.classList.remove("translate-x-full");
      document.body.style.overflow = "hidden";
      el.setAttribute("aria-hidden", "false");
    },

    close() {
      const el = this.el();
      const panel = this.panel();
      if (!el || !panel) return;
      el.classList.add("opacity-0", "pointer-events-none");
      el.classList.remove("opacity-100");
      panel.classList.add("translate-x-full");
      document.body.style.overflow = "";
      el.setAttribute("aria-hidden", "true");
    },

    rerender(sections) {
      if (!sections) return;
      const wasOpen = this.el()?.classList.contains("opacity-100");
      const targets = {
        "cart-drawer": "#shopify-section-cart-drawer",
        "cart-page": "#shopify-section-cart-page",
      };
      for (const [name, selector] of Object.entries(targets)) {
        const html = sections[name];
        const wrapper = $(selector);
        if (html && wrapper) {
          const tmp = document.createElement("div");
          tmp.innerHTML = html;
          const fresh = tmp.querySelector(selector);
          if (fresh) {
            wrapper.innerHTML = fresh.innerHTML;
          } else {
            wrapper.innerHTML = html;
          }
        }
      }
      this.bindEvents();
      if (wasOpen) {
        this.open();
      } else {
        document.body.style.overflow = "";
      }
    },

    bindEvents() {
      $$("[data-cart-drawer-close]").forEach((btn) =>
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.close();
        })
      );
      $$("[data-cart-increase]").forEach((btn) =>
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.changeLine(btn.dataset.cartIncrease, +1);
        })
      );
      $$("[data-cart-decrease]").forEach((btn) =>
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.changeLine(btn.dataset.cartDecrease, -1);
        })
      );
      $$("[data-cart-remove]").forEach((btn) =>
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.removeLine(btn.dataset.cartRemove);
        })
      );
    },

    async changeLine(key, delta) {
      const line = $(`[data-cart-line="${CSS.escape(key)}"]`);
      const qtySpan = line?.querySelector(".tabular-nums");
      const currentQty = parseInt(qtySpan?.textContent || "1", 10);
      const newQty = Math.max(0, currentQty + delta);
      return this.updateLine(key, newQty);
    },

    async removeLine(key) {
      return this.updateLine(key, 0);
    },

    async updateLine(key, quantity) {
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: "cart-drawer,cart-page",
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        this.rerender(data.sections);
        this.updateCartCount(data.item_count);
      } catch (e) {
        console.error("Cart update failed", e);
      }
    },

    updateCartCount(count) {
      const badge = $("[data-cart-count]");
      if (!badge) return;
      badge.textContent = String(count);
      badge.classList.toggle("hidden", count === 0);
    },

    async addItem(formData) {
      try {
        const body = new FormData(formData);
        body.append("sections", "cart-drawer,cart-page");
        body.append("sections_url", window.location.pathname);
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/javascript" },
          body,
        });
        const data = await res.json();
        if (data.status && data.status >= 400) {
          console.error("Add to cart failed", data);
          return null;
        }
        this.rerender(data.sections);
        const countRes = await fetch("/cart.js");
        const cart = await countRes.json();
        this.updateCartCount(cart.item_count);
        this.open();
        return cart;
      } catch (e) {
        console.error("Add to cart error", e);
        return null;
      }
    },
  };

  // Open drawer
  $$("[data-cart-drawer-open]").forEach((btn) =>
    btn.addEventListener("click", (e) => {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      cartDrawer.open();
    })
  );

  cartDrawer.bindEvents();

  // Expose for product configurator
  window.NevadesCart = cartDrawer;

  // ----- Product configurator -----
  $$("[data-configurator]").forEach((root) => {
    let product;
    try {
      product = JSON.parse(root.dataset.product);
    } catch (e) {
      console.error("Configurator: could not parse product data", e);
      return;
    }
    const form = root.querySelector("[data-configurator-form]");
    if (!form) return;

    const variantInput = form.querySelector("[data-variant-id]");
    const priceEl = form.querySelector("[data-price]");
    const installmentsEl = form.querySelector("[data-installments]");
    const addBtn = form.querySelector("[data-add-to-cart]");
    const addLabel = form.querySelector("[data-add-to-cart-label]");

    // Sitewide promo rate (e.g. 0.8 = −20 %), injected by main-product.liquid
    const promoRate = parseFloat(root.dataset.promoRate || "") || null;
    const renderPrice = (el, cents) => {
      if (!el) return;
      if (promoRate) {
        el.innerHTML =
          '<span style="text-decoration: line-through; opacity: .45; font-size: .62em; font-weight: 400;">' +
          formatPrice(cents) +
          "</span> " +
          formatPrice(Math.round(cents * promoRate));
      } else {
        el.textContent = formatPrice(cents);
      }
    };

    // Les blocs d'option ne sont plus dans l'ordre des options du produit
    // (couleurs affichées d'abord) : lecture par nom, pas par position.
    const optionBlocks = Array.from(form.querySelectorAll("[data-option-block]"));
    const selected = product.options.map((opt, i) => {
      const oname = typeof opt === "string" ? opt : (opt && opt.name);
      const block = optionBlocks.find((b) => b.dataset.optionName === oname);
      const active = block?.querySelector('[data-option-pick][aria-pressed="true"]');
      return active?.dataset.optionValue || product.options_with_values?.[i]?.values?.[0];
    });

    const findVariant = () =>
      product.variants.find((v) =>
        v.options.every((o, i) => o === selected[i])
      );

    // Index des options par nom (les options peuvent être des chaînes ou des objets)
    const optionIndex = (name) => {
      let idx = -1;
      (product.options || []).forEach((o, i) => {
        const n = typeof o === "string" ? o : (o && o.name);
        if (n === name) idx = i;
      });
      return idx;
    };
    const platIdx = optionIndex("Plateau");
    const pietIdx = optionIndex("Piétement");

    const readMap = (sel) => {
      const el = document.querySelector(sel);
      if (!el) return {};
      try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
    };

    const makeSlot = (triggerSel) => {
      const trigger = document.querySelector(triggerSel);
      const img = trigger ? trigger.querySelector("img") : null;
      return {
        trigger,
        img,
        def: img ? { src: img.src, full: trigger.dataset.fullSrc } : null,
        apply(entry) {
          if (!this.img) return;
          const e = entry || this.def;
          if (!e || !e.src) return;
          if (this.img.src !== e.src) {
            this.img.src = e.src;
            this.img.removeAttribute("srcset");
          }
          if (this.trigger) this.trigger.dataset.fullSrc = e.full || e.src;
        },
      };
    };

    // Photo principale : bascule sur la featured_image de la variante (combinaison)
    const mainSlot = makeSlot("[data-lightbox-gallery] [data-lightbox-trigger]");
    const swapMainImage = (variant) => {
      const fi = variant && variant.featured_image;
      mainSlot.apply(fi && fi.src ? { src: fi.src, full: fi.src } : null);
    };

    // Slot « piétement seul » : par couleur de piétement (images pietement-seul_<couleur>)
    const pietMap = readMap("[data-pietement-map]");
    const pietSlot = makeSlot("[data-pietement-slot]");
    const swapPietImage = () => {
      if (pietIdx < 0) return;
      pietSlot.apply(pietMap[selected[pietIdx]]);
    };

    // Slot « scène 2 » : par combinaison plateau|piétement (images <prefixe>2_plateau-x_pietement-y)
    const comboMap = readMap("[data-combo2-map]");
    const comboSlot = makeSlot("[data-combo-slot]");
    const swapComboImage = () => {
      if (platIdx < 0 || pietIdx < 0) return;
      comboSlot.apply(comboMap[selected[platIdx] + "|" + selected[pietIdx]]);
    };

    // Slot « scène 3 » : par combinaison plateau|piétement (images <prefixe>3_plateau-x_pietement-y)
    const combo3Map = readMap("[data-combo3-map]");
    const combo3Slot = makeSlot("[data-combo3-slot]");
    const swapCombo3Image = () => {
      if (platIdx < 0 || pietIdx < 0) return;
      combo3Slot.apply(combo3Map[selected[platIdx] + "|" + selected[pietIdx]]);
    };

    const updateUI = () => {
      const variant = findVariant();
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = "Combinaison indisponible";
        if (priceEl) priceEl.textContent = "—";
        if (installmentsEl) installmentsEl.textContent = "";
        return;
      }
      if (variantInput) variantInput.value = variant.id;
      if (addBtn) addBtn.disabled = false;
      if (addLabel) addLabel.textContent = "Ajouter au panier";
      renderPrice(priceEl, variant.price);
      if (installmentsEl) {
        const base = promoRate ? Math.round(variant.price * promoRate) : variant.price;
        const third = Math.round(base / 3);
        installmentsEl.textContent = `ou 3× ${formatPrice(third)} sans frais`;
      }
      const stickyPrice = $("[data-sticky-price]");
      renderPrice(stickyPrice, variant.price);
      // Photos : principale (combinaison), scènes 2 et 3 (combinaison), piétement (couleur)
      swapMainImage(variant);
      swapComboImage();
      swapCombo3Image();
      swapPietImage();
    };

    // Wire option picks
    form.querySelectorAll("[data-option-pick]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.optionValue;
        selected[idx] = value;

        const block = btn.closest("[data-option-block]");
        if (block) {
          block.querySelectorAll("[data-option-pick]").forEach((b) =>
            b.setAttribute("aria-pressed", b === btn ? "true" : "false")
          );

          block.querySelectorAll("[data-option-pick]").forEach((b) => {
            const isActive = b.getAttribute("aria-pressed") === "true";
            const isSwatch =
              b.classList.contains("rounded-full") && b.style.background;
            if (isSwatch) {
              if (isActive) {
                b.classList.add("border-accent", "scale-105");
                b.classList.remove("border-transparent", "ring-1", "ring-ink-15", "hover:ring-accent/40");
              } else {
                b.classList.remove("border-accent", "scale-105");
                b.classList.add("border-transparent", "ring-1", "ring-ink-15", "hover:ring-accent/40");
              }
            } else {
              if (isActive) {
                b.classList.add("border-accent", "bg-accent", "text-cream");
                b.classList.remove("border-ink-15", "hover:border-accent/50");
              } else {
                b.classList.remove("border-accent", "bg-accent", "text-cream");
                b.classList.add("border-ink-15", "hover:border-accent/50");
              }
            }
          });

          const display = block.querySelector("[data-option-value-display]");
          if (display) display.textContent = value;
        }

        updateUI();
      });
    });

    updateUI();

    // Add to cart via ajax + open drawer
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (addBtn) addBtn.disabled = true;
      if (addLabel) addLabel.textContent = "Ajout…";
      const result = await cartDrawer.addItem(form);
      if (result) {
        if (addLabel) addLabel.textContent = "Ajouté ✓";
        setTimeout(() => {
          if (addLabel) addLabel.textContent = "Ajouter au panier";
          if (addBtn) addBtn.disabled = false;
        }, 1800);
      } else {
        if (addLabel) addLabel.textContent = "Erreur — réessayer";
        if (addBtn) addBtn.disabled = false;
      }
    });
  });

  // ----- Search modal --------------------------------------------------------
  const searchModal = {
    el: () => $("[data-search-modal]"),
    panel: () => $("[data-search-panel]"),
    input: () => $("[data-search-input]"),
    debounceTimer: null,

    open() {
      const el = this.el();
      const panel = this.panel();
      const input = this.input();
      if (!el || !panel) return;
      el.classList.remove("opacity-0", "pointer-events-none");
      el.classList.add("opacity-100");
      panel.classList.remove("-translate-y-full");
      document.body.style.overflow = "hidden";
      el.setAttribute("aria-hidden", "false");
      setTimeout(() => input?.focus(), 150);
    },

    close() {
      const el = this.el();
      const panel = this.panel();
      if (!el || !panel) return;
      el.classList.add("opacity-0", "pointer-events-none");
      el.classList.remove("opacity-100");
      panel.classList.add("-translate-y-full");
      document.body.style.overflow = "";
      el.setAttribute("aria-hidden", "true");
    },

    showState(state) {
      const empty = $("[data-search-empty]");
      const loading = $("[data-search-loading]");
      const output = $("[data-search-output]");
      const noResults = $("[data-search-no-results]");
      [empty, loading, output, noResults].forEach((n) => n?.classList.add("hidden"));
      const target = { empty, loading, output, noResults }[state];
      target?.classList.remove("hidden");
    },

    async search(q) {
      clearTimeout(this.debounceTimer);
      const query = q.trim();
      if (!query) {
        this.showState("empty");
        return;
      }
      this.debounceTimer = setTimeout(async () => {
        this.showState("loading");
        try {
          const url =
            "/search/suggest.json?q=" + encodeURIComponent(query) +
            "&resources[type]=product,page,article" +
            "&resources[limit]=6&resources[options][unavailable_products]=last";
          const res = await fetch(url);
          const data = await res.json();
          this.render(data.resources?.results || {});
        } catch (e) {
          console.error("Search failed", e);
          this.showState("empty");
        }
      }, 220);
    },

    render(results) {
      const output = $("[data-search-output]");
      if (!output) return;

      const products = results.products || [];
      const pages = results.pages || [];
      const articles = results.articles || [];

      if (products.length === 0 && pages.length === 0 && articles.length === 0) {
        this.showState("noResults");
        return;
      }

      const escape = (s) =>
        String(s ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\"/g, "&quot;");

      const productHtml = products.length
        ? `<div>
            <p class="text-xs uppercase tracking-widest text-anthracite/55 mb-4">Produits (${products.length})</p>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              ${products
                .map(
                  (p) => `
                <a href="${escape(p.url)}" data-search-pick class="group block">
                  <div class="relative aspect-square rounded-2xl overflow-hidden bg-sand/30">
                    ${
                      p.featured_image?.url
                        ? `<img src="${escape(p.featured_image.url)}" alt="${escape(p.title)}" width="200" height="200" loading="lazy" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105">`
                        : ""
                    }
                  </div>
                  <p class="mt-3 text-sm font-medium leading-snug">${escape(p.title)}</p>
                  ${p.price ? `<p class="text-xs text-anthracite/60 mt-0.5">${escape(p.price)}</p>` : ""}
                </a>`
                )
                .join("")}
            </div>
          </div>`
        : "";

      const linkList = (items, label) =>
        items.length
          ? `<div>
              <p class="text-xs uppercase tracking-widest text-anthracite/55 mb-4">${label} (${items.length})</p>
              <ul class="divide-y divide-ink-08 border-t border-ink-08">
                ${items
                  .map(
                    (i) => `
                  <li>
                    <a href="${escape(i.url)}" data-search-pick class="flex items-center justify-between gap-4 py-4 group hover:text-anthracite">
                      <span class="font-medium">${escape(i.title)}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-4 h-4 text-anthracite/40 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
                    </a>
                  </li>`
                  )
                  .join("")}
              </ul>
            </div>`
          : "";

      output.innerHTML = productHtml + linkList(pages, "Pages") + linkList(articles, "Articles");
      this.showState("output");

      output.querySelectorAll("[data-search-pick]").forEach((a) =>
        a.addEventListener("click", () => this.close())
      );
    },

    init() {
      const input = this.input();
      if (!input) return;
      input.addEventListener("input", (e) => this.search(e.target.value));

      $$("[data-search-open]").forEach((btn) =>
        btn.addEventListener("click", () => this.open())
      );
      $$("[data-search-close]").forEach((btn) =>
        btn.addEventListener("click", () => this.close())
      );

      $$("[data-search-chip]").forEach((btn) =>
        btn.addEventListener("click", () => {
          const value = btn.dataset.searchChip;
          input.value = value;
          this.search(value);
        })
      );

      $$("[data-search-empty] [data-search-pick]").forEach((a) =>
        a.addEventListener("click", () => this.close())
      );

      document.addEventListener("keydown", (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
          e.preventDefault();
          this.open();
        }
        if (e.key === "Escape") this.close();
      });
    },
  };
  searchModal.init();

  // ----- Sticky mobile add-to-cart on PDP -----
  const stickyBar = $("[data-sticky-add-to-cart]");
  const configuratorRoot = $("[data-configurator]");
  if (stickyBar && configuratorRoot && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            stickyBar.classList.add("translate-y-full");
          } else if (entry.boundingClientRect.top < 0) {
            stickyBar.classList.remove("translate-y-full");
          }
        });
      },
      { threshold: 0 }
    );
    io.observe(configuratorRoot);

    const stickyBtn = stickyBar.querySelector("[data-sticky-add-button]");
    if (stickyBtn) {
      stickyBtn.addEventListener("click", () => {
        const form = $("[data-configurator-form]");
        if (form) form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      });
    }
  }

  // ----- Product gallery lightbox -------------------------------------------
  const lightbox = $("[data-lightbox]");
  if (lightbox) {
    const image = lightbox.querySelector("[data-lightbox-image]");
    const counter = lightbox.querySelector("[data-lightbox-counter]");
    const prevBtn = lightbox.querySelector("[data-lightbox-prev]");
    const nextBtn = lightbox.querySelector("[data-lightbox-next]");
    const sources = $$("[data-lightbox-sources] [data-full-src]").map((el) => ({
      src: el.dataset.fullSrc,
      alt: el.dataset.alt || "",
    }));
    let currentIndex = 0;

    const render = () => {
      if (!sources.length) return;
      const item = sources[currentIndex];
      image.src = item.src;
      image.alt = item.alt;
      if (counter && sources.length > 1) {
        counter.textContent = `${currentIndex + 1} / ${sources.length}`;
      } else if (counter) {
        counter.textContent = "";
      }
      if (prevBtn) prevBtn.style.visibility = sources.length > 1 ? "visible" : "hidden";
      if (nextBtn) nextBtn.style.visibility = sources.length > 1 ? "visible" : "hidden";
    };

    const open = (index) => {
      currentIndex = index || 0;
      render();
      lightbox.classList.remove("opacity-0", "pointer-events-none");
      lightbox.classList.add("opacity-100");
      document.body.style.overflow = "hidden";
    };

    const close = () => {
      lightbox.classList.add("opacity-0", "pointer-events-none");
      lightbox.classList.remove("opacity-100");
      document.body.style.overflow = "";
    };

    const next = () => {
      if (!sources.length) return;
      currentIndex = (currentIndex + 1) % sources.length;
      render();
    };

    const prev = () => {
      if (!sources.length) return;
      currentIndex = (currentIndex - 1 + sources.length) % sources.length;
      render();
    };

    $$("[data-lightbox-trigger]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = parseInt(btn.dataset.lightboxIndex || "0", 10);
        // Les vignettes peuvent avoir été remplacées par le configurateur
        // (photo de variante, scène 2/3, piétement coloré) : refléter la source à jour.
        if (btn.dataset.fullSrc && sources[idx]) {
          sources[idx].src = btn.dataset.fullSrc;
        }
        open(idx);
      });
    });

    lightbox.querySelectorAll("[data-lightbox-close]").forEach((el) =>
      el.addEventListener("click", close)
    );

    // Guard against accidental nav-button clicks after a pinch gesture.
    let gestureHadMultitouch = false;
    let lastMultitouchEndTime = 0;
    lightbox.addEventListener("touchstart", (e) => {
      if (e.touches.length === 1) gestureHadMultitouch = false;
      if (e.touches.length > 1) gestureHadMultitouch = true;
    }, { passive: true });
    lightbox.addEventListener("touchmove", (e) => {
      if (e.touches.length > 1) gestureHadMultitouch = true;
    }, { passive: true });
    lightbox.addEventListener("touchend", (e) => {
      if (e.touches.length === 0 && gestureHadMultitouch) {
        lastMultitouchEndTime = Date.now();
      }
    }, { passive: true });
    const guardedNav = (fn) => () => {
      if (Date.now() - lastMultitouchEndTime < 500) return;
      fn();
    };
    if (prevBtn) prevBtn.addEventListener("click", guardedNav(prev));
    if (nextBtn) nextBtn.addEventListener("click", guardedNav(next));

    document.addEventListener("keydown", (e) => {
      if (lightbox.classList.contains("pointer-events-none")) return;
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    });
  }
})();
